import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileCode, Save, X, ChevronLeft, ExternalLink, RefreshCw, GripVertical } from 'lucide-react';
import Editor from '@monaco-editor/react';
import * as api from '../services/api';
import type { Site } from '@wranglr/shared';
import '../styles/SiteEditor.css';

export default function SiteEditorPage() {
    const { siteId } = useParams<{ siteId: string }>();
    const navigate = useNavigate();

    const [site, setSite] = useState<Site | null>(null);
    const [files, setFiles] = useState<any[]>([]);
    const [selectedFile, setSelectedFile] = useState<string | null>(null);
    const [fileContent, setFileContent] = useState<string>('');
    const [originalContent, setOriginalContent] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewKey, setPreviewKey] = useState(0);
    const [activeTab, setActiveTab] = useState<'editor' | 'settings'>('editor');

    const sidebarRef = useRef<HTMLDivElement>(null);
    const hasUnsavedChanges = fileContent !== originalContent;

    useEffect(() => { loadData(); }, [siteId]);

    const loadData = async () => {
        if (!siteId) return;
        setLoading(true);
        try {
            const sitesRes = await api.getSites();
            if (sitesRes.success && sitesRes.data) {
                const currentSite = sitesRes.data.find(s => s.id === siteId);
                if (currentSite) setSite(currentSite);
            }
            const filesRes = await api.getFiles(siteId);
            if (filesRes.success && filesRes.data) {
                setFiles(filesRes.data);
                const indexFile = filesRes.data.find((f: any) => f.name === 'index.html');
                if (indexFile) handleSelectFile('index.html');
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleSelectFile = async (filename: string) => {
        if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Discard them?')) return;
        if (!siteId) return;
        setSelectedFile(filename);
        try {
            const content = await api.getFileContent(siteId, filename);
            setFileContent(content);
            setOriginalContent(content);
        } catch (err) {
            console.error('Failed to load file', err);
            setFileContent('');
            setOriginalContent('');
        }
    };

    const handleSave = async () => {
        if (!siteId || !selectedFile) return;
        setSaving(true);
        try {
            const res = await api.saveFileContent(siteId, selectedFile, fileContent);
            if (res.success) {
                setOriginalContent(fileContent);
                setPreviewKey(k => k + 1);
            } else {
                alert(res.error || 'Failed to save file');
            }
        } catch (err) { alert('Failed to save file'); }
        finally { setSaving(false); }
    };

    const startResize = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const ref = sidebarRef.current;
        if (!ref) return;
        const startX = e.clientX;
        const startWidth = ref.getBoundingClientRect().width;
        const onMouseMove = (ev: MouseEvent) => {
            const newWidth = Math.max(160, Math.min(startWidth + (ev.clientX - startX), 400));
            ref.style.width = `${newWidth}px`;
            ref.style.minWidth = `${newWidth}px`;
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, []);

    if (loading) return <div className="loading-screen">Loading Editor...</div>;
    if (!site) return <div className="loading-screen">Site not found.</div>;

    const liveUrl = `https://${site.domain_name}`;

    return (
        <div className="site-editor">
            <header className="editor-header">
                <div className="header-left">
                    <button className="btn btn-icon" onClick={() => navigate('/dashboard')} title="Back">
                        <ChevronLeft size={20} />
                    </button>
                    <div className="site-info">
                        <h2>{site.domain_name}</h2>
                    </div>
                </div>
                <div className="header-right">
                    {hasUnsavedChanges ? (
                        <>
                            <button className="btn btn-sm btn-cancel-header" onClick={() => { setFileContent(originalContent); }}>
                                <X size={14} /> Discard
                            </button>
                            <button className="btn btn-sm btn-save-header" onClick={handleSave} disabled={saving}>
                                <Save size={14} /> {saving ? 'Saving...' : 'Save'}
                            </button>
                        </>
                    ) : (
                        <span className="save-status">✓ Saved</span>
                    )}
                    <a href={liveUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-secondary">
                        <ExternalLink size={14} /> Live
                    </a>
                </div>
            </header>

            <div className="admin-tabs" style={{ display: 'flex', gap: '2px', padding: '0 16px', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
                {['editor'].map(tab => (
                    <button 
                        key={tab}
                        onClick={() => setActiveTab(tab as any)}
                        style={{
                            padding: '12px 20px',
                            background: activeTab === tab ? 'var(--bg-primary)' : 'transparent',
                            border: 'none',
                            borderTopLeftRadius: '6px',
                            borderTopRightRadius: '6px',
                            borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
                            color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-muted)',
                            cursor: 'pointer',
                            textTransform: 'capitalize',
                            fontWeight: activeTab === tab ? 600 : 400
                        }}
                    >
                        {tab === 'editor' ? 'Code Editor' : tab === 'settings' ? 'App Settings' : tab}
                    </button>
                ))}
            </div>

            <div className="editor-main" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {activeTab === 'editor' && (
                    <>
                {/* File Tree */}
                <div className="editor-sidebar" ref={sidebarRef} style={{ width: 220, minWidth: 220 }}>
                    <div className="panel-title">Explorer</div>
                    <div className="file-tree">
                        {files.map(file => (
                            <div
                                key={file.name}
                                className={`file-item ${selectedFile === file.name ? 'active' : ''}`}
                                onClick={() => handleSelectFile(file.name)}
                            >
                                <FileCode size={14} />
                                <span>{file.name}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Resize Handle */}
                <div className="resize-handle" onMouseDown={startResize}>
                    <GripVertical size={12} />
                </div>

                {/* Code Editor */}
                <div className="editor-code" style={{ flex: 1 }}>
                    {selectedFile ? (
                        <>
                            <div className="editor-code-header">
                                <span>{selectedFile}</span>
                                {hasUnsavedChanges && <span className="unsaved-dot" title="Unsaved changes"></span>}
                            </div>
                            <div className="editor-wrapper">
                                <Editor
                                    height="100%"
                                    language={selectedFile.endsWith('.css') ? 'css' : selectedFile.endsWith('.js') ? 'javascript' : selectedFile.endsWith('.json') ? 'json' : 'html'}
                                    theme="vs-dark"
                                    value={fileContent}
                                    onChange={(value) => setFileContent(value || '')}
                                    options={{
                                        minimap: { enabled: false },
                                        fontSize: 14,
                                        wordWrap: 'on',
                                        padding: { top: 16 },
                                        smoothScrolling: true,
                                        cursorSmoothCaretAnimation: 'on'
                                    }}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="editor-empty">
                            <FileCode size={48} />
                            <p>Select a file to start editing</p>
                        </div>
                    )}
                </div>

                {/* Preview */}
                <div className="editor-preview" style={{ flex: 1, minWidth: 200 }}>
                    <div className="preview-header">
                        <span className="preview-url">{liveUrl}</span>
                        <button className="btn btn-icon" onClick={() => setPreviewKey(k => k + 1)} title="Refresh">
                            <RefreshCw size={14} />
                        </button>
                    </div>
                    <div className="preview-iframe-wrapper">
                        <iframe
                            key={previewKey}
                            src={`${liveUrl}?_cb=${previewKey}-${Date.now()}`}
                            title="Live Preview"
                            className="preview-iframe"
                        />
                    </div>
                </div>
                </>
                )}
            </div>

            {/* Floating Save Bar */}
            {hasUnsavedChanges && (
                <div className="floating-save-bar">
                    <span>Unsaved changes in <strong>{selectedFile}</strong></span>
                    <div className="save-actions">
                        <button className="btn btn-ghost" onClick={() => setFileContent(originalContent)}>
                            <X size={16} /> Discard
                        </button>
                        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : <><Save size={16} /> Save & Deploy</>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
